import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiService } from "@/App";
import Header from "@/components/Header";
import { 
  Trophy, 
  ArrowLeft,
  TrendingUp,
  Medal,
  Crown,
  Bot,
  Flame
} from "lucide-react";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

export default function Leaderboard() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("all");

  useEffect(() => {
    fetchLeaderboard();
  }, [timeframe]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const res = await apiService.getLeaderboard(50, timeframe);
      setLeaderboard(res.data);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="font-mono text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-500/20 to-transparent border-gray-500/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/30';
    return 'bg-muted/30';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Stats */}
      <div className="hero-gradient py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-4xl font-bold tracking-wider mb-2">TOP TRADERS</h1>
          <p className="text-muted-foreground font-tech">Compete for the highest P&L on STXOSHI</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-6">
        {/* Timeframe Tabs */}
        <Tabs value={timeframe} onValueChange={setTimeframe} className="mb-6">
          <TabsList className="grid grid-cols-4 w-full bg-muted/50">
            <TabsTrigger value="daily" className="font-heading tracking-wide">24H</TabsTrigger>
            <TabsTrigger value="weekly" className="font-heading tracking-wide">7D</TabsTrigger>
            <TabsTrigger value="monthly" className="font-heading tracking-wide">30D</TabsTrigger>
            <TabsTrigger value="all" className="font-heading tracking-wide">ALL TIME</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* 2nd Place */}
            <Card 
              className={`glass-card ${getRankBg(2)} mt-8 cursor-pointer hover:scale-105 transition-transform`}
              onClick={() => navigate(`/profile/${leaderboard[1]?.user_id}`)}
            >
              <CardContent className="p-4 text-center">
                <Avatar className="w-16 h-16 mx-auto mb-2 border-2 border-gray-400">
                  <AvatarImage src={leaderboard[1]?.avatar_url} />
                  <AvatarFallback className="bg-gray-500/20 text-gray-400 font-heading text-xl">
                    {leaderboard[1]?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Medal className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <h3 className="font-semibold truncate">{leaderboard[1]?.username}</h3>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {leaderboard[1]?.stx_address?.slice(0, 8)}...
                </p>
                <p className="font-mono font-bold text-green-400 mt-2">
                  +{leaderboard[1]?.total_pnl?.toFixed(4)} sBTC
                </p>
              </CardContent>
            </Card>

            {/* 1st Place */}
            <Card 
              className={`glass-card ${getRankBg(1)} border-2 cursor-pointer hover:scale-105 transition-transform`}
              onClick={() => navigate(`/profile/${leaderboard[0]?.user_id}`)}
            >
              <CardContent className="p-4 text-center">
                <Crown className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                <Avatar className="w-20 h-20 mx-auto mb-2 border-2 border-yellow-400">
                  <AvatarImage src={leaderboard[0]?.avatar_url} />
                  <AvatarFallback className="bg-yellow-500/20 text-yellow-400 font-heading text-2xl">
                    {leaderboard[0]?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <h3 className="font-semibold text-lg truncate">{leaderboard[0]?.username}</h3>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {leaderboard[0]?.stx_address?.slice(0, 8)}...
                </p>
                <p className="font-mono font-bold text-green-400 text-xl mt-2">
                  +{leaderboard[0]?.total_pnl?.toFixed(4)} sBTC
                </p>
                <Badge className="mt-2 bg-yellow-500/20 text-yellow-400">
                  <Flame className="w-3 h-3 mr-1" />
                  {leaderboard[0]?.win_rate}% Win Rate
                </Badge>
              </CardContent>
            </Card>

            {/* 3rd Place */}
            <Card 
              className={`glass-card ${getRankBg(3)} mt-8 cursor-pointer hover:scale-105 transition-transform`}
              onClick={() => navigate(`/profile/${leaderboard[2]?.user_id}`)}
            >
              <CardContent className="p-4 text-center">
                <Avatar className="w-16 h-16 mx-auto mb-2 border-2 border-amber-600">
                  <AvatarImage src={leaderboard[2]?.avatar_url} />
                  <AvatarFallback className="bg-amber-600/20 text-amber-600 font-heading text-xl">
                    {leaderboard[2]?.username?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Medal className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <h3 className="font-semibold truncate">{leaderboard[2]?.username}</h3>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {leaderboard[2]?.stx_address?.slice(0, 8)}...
                </p>
                <p className="font-mono font-bold text-green-400 mt-2">
                  +{leaderboard[2]?.total_pnl?.toFixed(4)} sBTC
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Leaderboard */}
        <Card className="glass-card">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading tracking-wider">RANKINGS</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">Loading...</div>
            ) : leaderboard.length === 0 ? (
              <div className="p-12 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No rankings yet. Be the first!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {leaderboard.map((entry, index) => (
                  <div 
                    key={entry.user_id}
                    className={`p-3 sm:p-4 hover:bg-muted/30 transition-colors cursor-pointer ${getRankBg(entry.rank)}`}
                    onClick={() => navigate(`/profile/${entry.user_id}`)}
                    data-testid={`leaderboard-entry-${index}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                        <div className="w-8 sm:w-10 flex justify-center flex-shrink-0">
                          {getRankIcon(entry.rank)}
                        </div>
                        <Avatar className="w-8 h-8 sm:w-10 sm:h-10 hidden sm:flex flex-shrink-0">
                          <AvatarImage src={entry.avatar_url} />
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {entry.username?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold truncate text-xs sm:text-sm">{entry.username}</h3>
                          <p className="text-xs text-muted-foreground font-mono truncate">
                            {entry.stx_address?.slice(0, 10)}...
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-muted-foreground">Agents</p>
                          <p className="font-mono font-bold text-xs sm:text-sm">{entry.agents_count}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground hidden sm:block">Win</p>
                          <p className="font-mono font-bold text-xs sm:text-sm">{entry.win_rate}%</p>
                        </div>
                        <div className="text-right min-w-[55px] sm:min-w-[70px]">
                          <p className="text-xs text-muted-foreground hidden sm:block">P&L</p>
                          <p className={`font-mono font-bold text-xs sm:text-sm ${entry.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {entry.total_pnl >= 0 ? '+' : ''}{entry.total_pnl?.toFixed(4)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
